import { EIGENFIELD } from './eigenfield-recipe';

/**
 * The same field, looked at from somewhere else.
 *
 * The landing page carries the field down its whole length — hero, showcase,
 * problem, FAQ. Four copies of one picture reads as a tiled background rather
 * than as a motif, so each section gets its own superposition.
 *
 * ── Only three knobs move, and one of them is why ────────────────────────────
 * `freq` and `amp` are NOT variables here. `freq` is the stripe period, so
 * changing it changes how big the drawing is; the sections would stop looking
 * like one family. `amp` is worse: the recipe's comment ("already capped at the
 * fold margin") means the baked value sits right at the point where the
 * displacement is about to push a line past its neighbour, and a larger one
 * folds the field back on itself — visible as lines crossing, which no test
 * would catch and which does not look like a bug so much as a mistake.
 *
 * That leaves:
 *
 *   weights  which of the four room modes dominates. This is the real variance:
 *            the contours reorganise completely while every line keeps the same
 *            width and spacing.
 *   tilt     the whole field's rotation.
 *   rot      whether the modes are sampled transposed, which turns the drawing
 *            through its other axis rather than merely spinning it.
 *
 * Every variant's weights are a PERMUTATION (with signs) of the recipe's own, so
 * the sum of magnitudes is identical to the baked one by construction — the fold
 * cap `amp` was measured against therefore still holds, and cannot be broken by
 * someone hand-tuning a number here. `eigenfield-variants.test.ts` pins that.
 */
export type EigenfieldVariant = {
  weights: readonly [number, number, number, number];
  tilt: number;
  rot: boolean;
};

export const EIGENFIELD_VARIANTS = {
  /** The baked recipe, unchanged — the hero's. */
  hero: {
    weights: EIGENFIELD.weights as readonly [number, number, number, number],
    tilt: EIGENFIELD.tilt,
    rot: EIGENFIELD.rot,
  },
  /** Third mode leading, laid over hard. The most diagonal of the four. */
  showcase: {
    weights: [0.25, -0.2, 0.6, 0.3],
    tilt: 0.6,
    rot: EIGENFIELD.rot,
  },
  /** Second mode leading, tilted the other way, so the fan opens left where the
   *  hero's opens right. */
  problem: {
    weights: [0.3, 0.6, -0.2, 0.25],
    tilt: -0.2,
    rot: EIGENFIELD.rot,
  },
  /** Fourth mode leading — the widest curves — at the steepest tilt. */
  faq: {
    weights: [-0.2, 0.25, 0.3, 0.6],
    tilt: 0.78,
    rot: EIGENFIELD.rot,
  },
} as const satisfies Record<string, EigenfieldVariant>;

export type EigenfieldVariantName = keyof typeof EIGENFIELD_VARIANTS;
